import {
  Button,
  Css,
  Filters,
  ModalBody,
  ModalFooter,
  ModalHeader,
  multiFilter,
  OffsetAndLimit,
  Pagination,
  SelectCardGroup,
  TagType,
  useModal,
} from "@homebound/beam";
import { useMemo, useState } from "react";
import { SearchBox } from "src/components";
import {
  SelectProductModalProjectItems_ProjectItemFragment,
  useSelectProductModalMetadataQuery,
  useSelectProductModalProjectItemsQuery,
} from "src/routes/projects/change-events/guided/workflows/swapProductCatalog/components/SelectProductModal.generated";
import { queryResult } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";

type ProductFilter = { costCode?: string[] };

type SelectProductModalProps = {
  projectId: string;
  onSelect: (productId: string) => void;
  initialSelectedProductId?: string;
  /** PIs already staged in another swap. A product can't be swapped twice, so we leave them out. */
  excludedProjectItemIds?: string[];
};

export function SelectProductModal(props: SelectProductModalProps) {
  const { projectId, onSelect, initialSelectedProductId = null, excludedProjectItemIds = [] } = props;
  const { closeModal } = useModal();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ProductFilter>({});
  const [pageSettings, setPageSettings] = useState<OffsetAndLimit>({ limit: 100, offset: 0 });
  const [selectedProductId, setSelectedProductId] = useState<string | null>(initialSelectedProductId);

  const metadataQuery = useSelectProductModalMetadataQuery({ variables: { projectId }, fetchPolicy: "cache-first" });
  const projectItemsQuery = useSelectProductModalProjectItemsQuery({
    variables: {
      filter: {
        project: [projectId],
        isProduct: true,
        hasBids: true,
        isRemovedScope: false,
        search,
        costCode: filter.costCode,
      },
      pageFilter: pageSettings,
    },
    // `projectItemsPage` has an infinite-scroll type policy that appends each page to the prior ones,
    // which would show page 1 + page 2 together. Skip the cache so we only render the current page.
    fetchPolicy: "no-cache",
  });

  const filterDefs = useMemo(
    () => ({
      costCode: multiFilter({
        label: "Cost Code",
        options:
          metadataQuery.data?.projectCostCodes.entities.map((pcc) => ({
            value: pcc.costCode.id,
            label: pcc.costCode.name,
          })) ?? [],
        getOptionLabel: (o) => o.label,
        getOptionValue: (o) => o.value,
      }),
    }),
    [metadataQuery],
  );

  return (
    <>
      <ModalHeader>Select the Product to Replace</ModalHeader>
      <ModalBody>
        <div css={Css.df.fdc.gap2.$}>
          <div css={Css.df.aic.gap2.$}>
            <SearchBox onSearch={setSearch} />
            <Filters<ProductFilter> filter={filter} filterDefs={filterDefs} onChange={setFilter} />
          </div>
          {queryResult(projectItemsQuery, ({ projectItemsPage }) => (
            <ProductList
              projectItems={projectItemsPage.projectItems}
              excludedProjectItemIds={excludedProjectItemIds}
              selectedProductId={selectedProductId}
              onChange={setSelectedProductId}
              totalCount={projectItemsPage.pageInfo.totalCount ?? 0}
            />
          ))}
        </div>
      </ModalBody>
      <ModalFooter>
        <div css={Css.df.w100.fdc.$}>
          <Pagination
            page={[pageSettings, setPageSettings]}
            totalCount={projectItemsQuery.data?.projectItemsPage.pageInfo.totalCount ?? 0}
          />
          <div css={Css.df.mt2.jcfe.$}>
            <Button label="Cancel" variant="quaternary" onClick={() => closeModal()} />
            <Button
              label={"Select"}
              disabled={!selectedProductId}
              onClick={() => {
                onSelect(selectedProductId!);
                closeModal();
              }}
            />
          </div>
        </div>
      </ModalFooter>
    </>
  );
}

type ProductListProps = {
  projectItems: SelectProductModalProjectItems_ProjectItemFragment[];
  totalCount: number;
  excludedProjectItemIds: string[];
  selectedProductId: string | null;
  onChange: (productId: string | null) => void;
};

function ProductList(props: ProductListProps) {
  const { projectItems, totalCount, excludedProjectItemIds, selectedProductId, onChange } = props;
  const options = useMemo(
    () =>
      projectItems
        .filter((pi) => !excludedProjectItemIds.includes(pi.id))
        .map((pi) => {
          return {
            value: pi.id,
            label: pi.materialVariant?.listing.brand?.name ?? pi.materialVariant?.listing.name ?? "",
            description: (
              <span css={Css.df.fdc.$}>
                <span>{pi.materialVariant?.displayName ?? ""}</span>
                <span css={Css.xs.gray700.$}>{pi.location.displayLocationPath}</span>
              </span>
            ),
            image: pi.materialVariant?.featuredImage?.asset?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL,
            ...([pi, ...pi.planBasedCostHoldingProjectItems].some((pi) => pi.allCommittedCostInCents > 0)
              ? {
                  tag: { text: "PO ISSUED", type: "info" as TagType },
                  tooltip:
                    "This product has already been included on a Purchase Order. Swapping it may require updating the existing Purchase Order.",
                }
              : {}),
          };
        }),
    [projectItems, excludedProjectItemIds],
  );
  return (
    <SelectCardGroup
      label={`${totalCount} results`}
      data-testid="products"
      options={options}
      value={selectedProductId}
      onChange={onChange}
      layout="horizontal"
      view="grid"
    />
  );
}
